import React, { useState, useRef, useEffect } from 'react';

const StateSelector = ({ value = [], onChange, placeholder = "Type to search states..." }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);



  // All US states for reference
  const allStates = [
    { code: 'AL', name: 'Alabama' },
    { code: 'AK', name: 'Alaska' },
    { code: 'AZ', name: 'Arizona' },
    { code: 'AR', name: 'Arkansas' },
    { code: 'CA', name: 'California' },
    { code: 'CO', name: 'Colorado' },
    { code: 'CT', name: 'Connecticut' },
    { code: 'DE', name: 'Delaware' },
    { code: 'DC', name: 'District of Columbia' },
    { code: 'FL', name: 'Florida' },
    { code: 'GA', name: 'Georgia' },
    { code: 'HI', name: 'Hawaii' },
    { code: 'ID', name: 'Idaho' },
    { code: 'IL', name: 'Illinois' },
    { code: 'IN', name: 'Indiana' },
    { code: 'IA', name: 'Iowa' },
    { code: 'KS', name: 'Kansas' },
    { code: 'KY', name: 'Kentucky' },
    { code: 'LA', name: 'Louisiana' },
    { code: 'ME', name: 'Maine' },
    { code: 'MD', name: 'Maryland' },
    { code: 'MA', name: 'Massachusetts' },
    { code: 'MI', name: 'Michigan' },
    { code: 'MN', name: 'Minnesota' },
    { code: 'MS', name: 'Mississippi' },
    { code: 'MO', name: 'Missouri' },
    { code: 'MT', name: 'Montana' },
    { code: 'NE', name: 'Nebraska' },
    { code: 'NV', name: 'Nevada' },
    { code: 'NH', name: 'New Hampshire' },
    { code: 'NJ', name: 'New Jersey' },
    { code: 'NM', name: 'New Mexico' },
    { code: 'NY', name: 'New York' },
    { code: 'NC', name: 'North Carolina' },
    { code: 'ND', name: 'North Dakota' },
    { code: 'OH', name: 'Ohio' },
    { code: 'OK', name: 'Oklahoma' },
    { code: 'OR', name: 'Oregon' },
    { code: 'PA', name: 'Pennsylvania' },
    { code: 'RI', name: 'Rhode Island' },
    { code: 'SC', name: 'South Carolina' },
    { code: 'SD', name: 'South Dakota' },
    { code: 'TN', name: 'Tennessee' },
    { code: 'TX', name: 'Texas' },
    { code: 'UT', name: 'Utah' },
    { code: 'VT', name: 'Vermont' },
    { code: 'VA', name: 'Virginia' },
    { code: 'WA', name: 'Washington' },
    { code: 'WV', name: 'West Virginia' },
    { code: 'WI', name: 'Wisconsin' },
    { code: 'WY', name: 'Wyoming' },
    { code: 'PR', name: 'Puerto Rico' },
    { code: 'UK', name: 'United Kingdom' },
    { code: 'CA_COUNTRY', name: 'Canada' }
  ];

  // Filter states based on search term
  const filteredStates = allStates.filter(state => {
    const searchLower = searchTerm.toLowerCase();
    return (
      state.name.toLowerCase().includes(searchLower) ||
      state.code.toLowerCase().includes(searchLower)
    );
  });





  // Handle state selection/deselection
  const handleStateToggle = (stateCode) => {
    let newValue;
    if (value.includes(stateCode)) {
      // Remove state
      newValue = value.filter(code => code !== stateCode);
    } else {
      // Add state
      newValue = [...value, stateCode];
    }
    onChange(newValue);
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Reset highlighted index when search term changes
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [searchTerm]);

  // Auto-scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && dropdownRef.current) {
      const highlightedElement = dropdownRef.current.querySelector(`[data-index="${highlightedIndex}"]`);
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  // Handle keyboard navigation
  const handleKeyDown = (event) => {
    if (!isOpen) {
      if (event.key === 'ArrowDown' || event.key === 'Enter') {
        setIsOpen(true);
        setHighlightedIndex(0);
        event.preventDefault();
      }
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredStates.length - 1 ? prev + 1 : prev
        );
        break;
      
      case 'ArrowUp':
        event.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      
      case 'Enter':
        event.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredStates.length) {
          const selectedState = filteredStates[highlightedIndex];
          handleStateToggle(selectedState.code);
          setSearchTerm('');
          setHighlightedIndex(-1);
        }
        break;
      
      case 'Escape':
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
        break;
      
      case 'Tab':
        // Close dropdown and allow natural tab navigation
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
        // Don't prevent default - let Tab work normally to move to next focusable element
        break;
      
      default:
        // Reset highlighted index when typing
        setHighlightedIndex(-1);
        break;
    }
  };

  const selectedStatesDisplay = value.map(code => {
    const state = allStates.find(s => s.code === code);
    return state ? `${state.name} (${state.code})` : code;
  }).join(', ');



  return (
    <div className="relative" ref={dropdownRef}>
      {/* Selected States Display */}
      {value.length > 0 && (
        <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="text-sm font-medium text-blue-800 mb-1">
            Selected States:
          </div>
          <div className="text-sm text-blue-700">
            {selectedStatesDisplay}
          </div>
        </div>
      )}

      {/* Search Input */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
                  onChange={(e) => {
          setSearchTerm(e.target.value);
          if (!isOpen) setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          <svg 
            className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {filteredStates.length > 0 ? (
            <div className="py-1">
              {filteredStates.map((state, index) => {
                const isSelected = value.includes(state.code);
                const isHighlighted = index === highlightedIndex;
                
                return (
                  <div
                    key={state.code}
                    data-index={index}
                    onClick={() => handleStateToggle(state.code)}
                    className={`flex items-center px-4 py-2 cursor-pointer ${
                      isHighlighted ? 'bg-blue-100' : 'hover:bg-gray-50'
                    } ${isSelected ? 'bg-blue-50' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {}} // Handled by onClick above
                      className="w-4 h-4 text-blue-600 mr-3"
                      tabIndex="-1"
                    />
                    <span className="text-gray-900">
                      {state.name} ({state.code})
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="px-4 py-3 text-gray-500 text-center">
              No states found matching "{searchTerm}"
            </div>
          )}
        </div>
      )}

      {/* Help Text */}
      <div className="mt-2 text-sm text-gray-600">
        Type state name or abbreviation to search. Use arrow keys and Enter to select, Tab to finish, or click with mouse. Select multiple states where your organization operates.
      </div>
    </div>
  );
};

export default StateSelector;
